import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, TrendingUp, Star, Trophy, ArrowRight, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StudentDashboard({ user }) {
  const [enrollments, setEnrollments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [enrollmentsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/enrollments/my`, { withCredentials: true }),
        axios.get(`${API}/bookings/my`, { withCredentials: true })
      ]);
      setEnrollments(enrollmentsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="student-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
            Student Dashboard
          </span>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: 'Fraunces, serif' }}
            data-testid="dashboard-title"
          >
            Welcome, <span className="gold-gradient">{user?.name}</span>
          </h1>
          <p className="text-white/60 mt-2">Track your learning journey and achieve your goals</p>
        </div>
        
        {/* Quick Action - Level Progress */}
        <Link to="/my-levels" className="block mb-8" data-testid="level-progress-link">
          <div className="bg-dark-surface text-white p-6 rounded-lg transition-all duration-300 border-l-4 border-premium-gold hover:shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Trophy className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                    Ready to Level Up?
                  </h3>
                  <p className="text-soft-lavender text-sm mt-1">Submit proof of your achievements to progress to the next level. Your coach will review and approve your advancement.</p>
                </div>
              </div>
              <button className="px-6 py-2 bg-transparent border border-premium-gold text-premium-gold rounded-lg font-semibold hover:bg-premium-gold hover:text-deep-dark transition-all flex items-center gap-2">
                Submit Level Proof <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </Link>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/20" data-testid="stat-courses">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-soft-lavender font-semibold mb-1">Enrolled courses</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {enrollments.length}
                </p>
              </div>
              <BookOpen className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            </div>
          </div>
          
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/20" data-testid="stat-sessions">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-soft-lavender font-semibold mb-1">Upcoming sessions</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {bookings.filter(b => b.status === 'scheduled').length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            </div>
          </div>
          
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/20" data-testid="stat-progress">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-soft-lavender font-semibold mb-1">Avg progress</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {enrollments.length > 0 
                    ? Math.round(enrollments.reduce((sum, e) => sum + e.progress_percentage, 0) / enrollments.length)
                    : 0}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            </div>
          </div>
        </div>
        
        {/* My Courses */}
        <div className="mb-12">
          <h2 
            className="text-3xl font-bold tracking-tight text-white mb-6 flex items-center gap-3"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            <Zap className="w-7 h-7 text-premium-gold" />
            My Courses
          </h2>
          
          {enrollments.length === 0 ? (
            <div className="bg-dark-surface p-12 text-center rounded-xl border border-premium-gold/30/50" data-testid="no-enrollments">
              <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-xl text-white/60 mb-6">You haven't enrolled in any courses yet</p>
              <Link to="/courses">
                <button className="bg-premium-gold hover:bg-premium-gold-dark text-white font-bold tracking-wider px-8 py-3 rounded-lg transition-all duration-300 hover:shadow-glow-red">
                  Browse Courses
                </button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {enrollments.map((enrollment) => (
                <div key={enrollment.enrollment_id} className="bg-dark-surface rounded-xl overflow-hidden border border-premium-gold/30/50 hover:border-premium-gold/30 transition-all duration-300" data-testid={`enrollment-${enrollment.enrollment_id}`}>
                  <div className="h-40 bg-deep-dark overflow-hidden">
                    {enrollment.course_image ? (
                      <img src={enrollment.course_image} alt={enrollment.course_title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-imperial to-dark-purple">
                        <BookOpen className="w-12 h-12 text-white/20" strokeWidth={1.5} />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                      {enrollment.course_title}
                    </h3>
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-white/60">Progress</span>
                        <span className="font-bold text-premium-gold">{enrollment.progress_percentage}%</span>
                      </div>
                      <div className="w-full bg-deep-dark rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-premium-gold to-yellow-400 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${enrollment.progress_percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <Link to={`/courses/${enrollment.course_id}`}>
                      <button className="w-full bg-premium-gold hover:bg-premium-gold-dark text-white font-semibold py-3 rounded-lg transition-all duration-300">
                        Continue Learning
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Upcoming Sessions */}
        {bookings.filter(b => b.status === 'scheduled').length > 0 && (
          <div>
            <h2 
              className="text-3xl font-bold tracking-tight text-white mb-6 flex items-center gap-3"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              <Calendar className="w-7 h-7 text-premium-gold" strokeWidth={1.5} />
              Upcoming Sessions
            </h2>
            <div className="space-y-4">
              {bookings
                .filter(b => b.status === 'scheduled')
                .map((booking) => (
                  <div key={booking.booking_id} className="bg-dark-surface p-6 rounded-xl border border-premium-gold/30/50 hover:border-premium-gold/30 transition-all" data-testid={`booking-${booking.booking_id}`}>
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                          {booking.course_title}
                        </h3>
                        <p className="text-white/60">with {booking.coach_name}</p>
                        <p className="text-sm text-premium-gold mt-2 flex items-center gap-2">
                          <Calendar className="w-4 h-4" strokeWidth={1.5} />
                          {new Date(booking.scheduled_at).toLocaleString()} • {booking.duration_minutes} mins
                        </p>
                      </div>
                      {booking.meeting_link && (
                        <a 
                          href={booking.meeting_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="bg-premium-gold hover:bg-premium-gold-dark text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-glow-red flex items-center gap-2"
                        >
                          Join Session <ArrowRight className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentDashboard;
